// WorkspacePrefsBanner — top-of-page banner shown on /workspace.
//
// Two responsibilities (deliberately bundled into one component to keep
// the visual hierarchy of /workspace clean — the user only sees one
// dismissible strip, not two stacked ones):
//
//   1. **Welcome / first-visit cue.** First time a user lands here we
//      pitch what a "workspace" is. Persisted via localStorage so it
//      doesn't keep re-appearing.
//   2. **Preferences summary.** Once dismissed (or on subsequent
//      visits), we read the user's default workspace from the backend
//      and surface their seeded preferences (budget/areas/timeline/
//      priorities) as a compact chip list. This is the FIRST piece of
//      Workspace.tsx wired to real backend data — properties/units/
//      viewings remain mock for now (P2 work).

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { apiJson } from "@/lib/api";
import { PrefsEditModal } from "./PrefsEditModal";
import { areaLabel, type Translate } from "@/lib/areaOptions";
import { STORAGE_ONBOARDING } from "@/constants/storage";

const LS_DISMISSED_KEY = STORAGE_ONBOARDING.workspaceWelcomeDismissed;
// One-time nudge surface: shown when the prior signup attempt to seed
// the workspace's preferences failed (DoneScreen sets the flag).
const LS_SEED_FAILED_KEY = STORAGE_ONBOARDING.prefsSeedFailed;
// Set when the user explicitly chose "Skip" on the signup PreferencesScreen.
// Surfaces a soft CTA inviting them to fill in prefs from the workspace.
const LS_PREFS_SKIPPED_KEY = STORAGE_ONBOARDING.prefsSkipped;

interface BackendWorkspace {
  id: string;
  name: string;
  intent: string;
  budget_min?: number | null;
  budget_max?: number | null;
  areas: string;
  timeline: string;
  priorities: unknown; // raw JSON: string[] expected
}

function formatBudget(min?: number | null, max?: number | null): string | null {
  if (min == null && max == null) return null;
  const fmt = (n: number) =>
    n >= 1_000_000 ? `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`
    : n >= 1_000 ? `${Math.round(n / 1_000)}k`
    : `${n}`;
  if (min != null && max != null) return `S$${fmt(min)} – ${fmt(max)}`;
  if (max != null) return `≤ S$${fmt(max)}`;
  return `≥ S$${fmt(min!)}`;
}

export function WorkspacePrefsBanner({ onEdit: onEditExt }: { onEdit?: () => void } = {}) {
  const { t } = useTranslation();
  // /workspace/:wsId — when present, banner must show *that* workspace's
  // prefs, not the first one. Falls back to first workspace on /workspace.
  const { wsId } = useParams<{ wsId: string }>();
  const [welcomeDismissed, setWelcomeDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem(LS_DISMISSED_KEY) === "1";
  });
  const [ws, setWs] = useState<BackendWorkspace | null>(null);
  // Nudge banner state:
  //   - "seed-failed": signup PATCH didn't apply, user lands here without prefs
  //   - "skipped": user pressed Skip on signup; gentle CTA to complete now
  // Both clear after dismissal so they don't keep nagging.
  const [seedFailed, setSeedFailed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(LS_SEED_FAILED_KEY) === "1"; } catch { return false; }
  });
  const [skipped, setSkipped] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(LS_PREFS_SKIPPED_KEY) === "1"; } catch { return false; }
  });
  // Local edit modal (A4). Bumping `reload` forces a re-fetch after save
  // so the bar reflects new values without a page refresh.
  const [editing, setEditing] = useState(false);
  const [reload, setReload] = useState(0);
  // The component owns its own onEdit by default. The optional external
  // onEditExt prop lets a parent (e.g. workspace page header) trigger
  // the same modal — we merge the two into a single openEdit() handler.
  const openEdit = () => {
    if (onEditExt) onEditExt();
    setEditing(true);
  };

  useEffect(() => {
    let cancelled = false;
    apiJson<{ workspaces: BackendWorkspace[] }>("/workspaces")
      .then((res) => {
        if (cancelled) return;
        if (Array.isArray(res?.workspaces) && res.workspaces.length > 0) {
          // Prefer the routed workspace; fall back to first if wsId is
          // missing or doesn't match (e.g. legacy URL after deletion).
          const matched = wsId
            ? res.workspaces.find((w) => w.id === wsId)
            : undefined;
          setWs(matched ?? res.workspaces[0]);
        }
      })
      .catch(() => {
        // Non-fatal: banner just won't show prefs. Workspace page still
        // works against mock data. We intentionally don't surface the
        // error — the GET is best-effort.
      });
    return () => {
      cancelled = true;
    };
  }, [wsId, reload]);

  const dismissSeedFailed = () => {
    setSeedFailed(false);
    try { window.localStorage.removeItem(LS_SEED_FAILED_KEY); } catch { /* ignore */ }
  };
  const dismissSkipped = () => {
    setSkipped(false);
    try { window.localStorage.removeItem(LS_PREFS_SKIPPED_KEY); } catch { /* ignore */ }
  };

  const dismiss = () => {
    setWelcomeDismissed(true);
    try {
      window.localStorage.setItem(LS_DISMISSED_KEY, "1");
    } catch {
      // localStorage may be disabled (private mode); fall back to in-memory.
    }
  };

  // EditModalSlot — co-rendered alongside any return path so the modal
  // can be opened from any nudge / banner state without prop-drilling.
  const editModalSlot = editing && ws ? (
    <PrefsEditModal
      workspaceId={ws.id}
      initial={{
        budget_min: ws.budget_min ?? null,
        budget_max: ws.budget_max ?? null,
        areas: ws.areas ?? "",
        timeline: ws.timeline ?? "",
        priorities: Array.isArray(ws.priorities)
          ? (ws.priorities as unknown[]).filter((x): x is string => typeof x === "string")
          : [],
      }}
      onClose={() => setEditing(false)}
      onSaved={() => setReload((n) => n + 1)}
    />
  ) : null;

  // ── WELCOME state (first visit, before dismissal) ──────────────────────────
  if (!welcomeDismissed) {
    return (
      <>
        <div
          role="status"
          data-tour-id="workspace.welcome"
          // Light-surface palette: the consumer shell renders on a pale
          // cream/pink bg, so the previous text-emerald-100/200 tokens
          // (designed for a dark surface) were near-invisible. Saturated
          // 700/900 ink on a 50-tint band gives WCAG-AA contrast.
          className="flex items-center gap-2.5 border-b border-emerald-200 bg-emerald-50 px-4 py-1.5 text-[12.5px] text-emerald-900"
        >
          <span className="text-[13px]" aria-hidden>👋</span>
          <div className="flex-1 min-w-0 truncate">
            <span className="font-semibold">{t("workspace.welcome.title")}</span>
            <span className="ml-1.5 text-[11.5px] text-emerald-700">
              {t("workspace.welcome.body")}
            </span>
          </div>
          <button
            onClick={dismiss}
            aria-label={t("common.close")}
            className="shrink-0 rounded-md px-2 py-0.5 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100"
          >
            {t("workspace.welcome.dismiss")}
          </button>
        </div>
        {editModalSlot}
      </>
    );
  }

  // ── SEED-FAILED nudge (A2): signup tried to PATCH prefs but server rejected
  // it; user is now on /workspace with no seeded data. One-shot, dismissible.
  if (seedFailed) {
    return (
      <>
        <div
          role="status"
          data-tour-id="workspace.seedFailedBanner"
          // Light-surface amber: solid 600 CTA + 700/900 ink for AA contrast.
          className="flex items-center gap-2.5 border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-[12.5px] text-amber-900"
        >
          <span className="text-[13px]" aria-hidden>⚠️</span>
          <div className="flex-1 min-w-0 truncate">
            <span className="font-semibold">{t("workspace.seedFailed.title")}</span>
            <span className="ml-1.5 text-[11.5px] text-amber-700">{t("workspace.seedFailed.body")}</span>
          </div>
          <button
            onClick={() => { openEdit(); dismissSeedFailed(); }}
            className="shrink-0 rounded-md bg-amber-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-amber-700"
          >
            {t("workspace.seedFailed.cta")}
          </button>
          <button
            onClick={dismissSeedFailed}
            aria-label={t("common.close")}
            className="shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-100"
          >
            {t("workspace.welcome.dismiss")}
          </button>
        </div>
        {editModalSlot}
      </>
    );
  }

  // ── PREFS state (post-dismissal): show the seeded preferences ──────────────
  if (!ws) return null;

  const budget = formatBudget(ws.budget_min, ws.budget_max);
  const areas = (ws.areas || "")
    .split(",")
    .map((a) => a.trim())
    .filter(Boolean);
  let priorities: string[] = [];
  if (Array.isArray(ws.priorities)) {
    priorities = (ws.priorities as unknown[]).filter(
      (x): x is string => typeof x === "string"
    );
  }

  // If the user skipped preferences entirely, the workspace exists but is
  // empty. A5: surface a soft CTA inviting them to fill in prefs (only if
  // we know they explicitly skipped during signup).
  const hasAnyPref = budget || areas.length > 0 || ws.timeline || priorities.length > 0;
  if (!hasAnyPref) {
    if (skipped) {
      return (
        <>
          <div
            role="status"
            data-tour-id="workspace.skippedNudge"
            // Light-surface variant of the welcome strip — same palette as
            // the welcome banner so the visual rhythm reads as "same family".
            className="flex items-center gap-3 border-b border-emerald-200 bg-emerald-50 px-4 py-2 text-[12px] text-emerald-900"
          >
            <span className="text-base" aria-hidden>✨</span>
            <div className="flex-1 min-w-0">
              <span className="font-semibold">{t("workspace.skippedPrefs.title")}</span>
              <span className="ml-1.5 text-emerald-700">{t("workspace.skippedPrefs.body")}</span>
            </div>
            <button
              onClick={() => { openEdit(); dismissSkipped(); }}
              className="rounded-md bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700"
            >
              {t("workspace.skippedPrefs.cta")}
            </button>
            <button
              onClick={dismissSkipped}
              aria-label={t("common.close")}
              className="rounded-md px-2 py-1 text-[11px] font-medium text-emerald-800 hover:bg-emerald-100"
            >
              ×
            </button>
          </div>
          {editModalSlot}
        </>
      );
    }
    return editModalSlot;
  }

  return (
    <>
    <div
      role="status"
      data-tour-id="workspace.prefs"
      className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-border bg-surface-2 px-4 py-2 text-[12px] text-text-sec"
    >
      <span className="text-[10.5px] font-semibold uppercase tracking-widest text-text-muted">
        {t("workspace.prefsBar.label")}
      </span>
      {budget && (
        <span className="inline-flex items-center gap-1.5">
          <span className="text-text-muted">{t("workspace.prefsBar.budget")}</span>
          <span className="font-medium text-text">{budget}</span>
        </span>
      )}
      {areas.length > 0 && (
        <span className="inline-flex items-center gap-1.5">
          <span className="text-text-muted">{t("workspace.prefsBar.areas")}</span>
          <span className="font-medium text-text">
            {areas.map((a) => areaLabel(a, t as Translate)).join(", ")}
          </span>
        </span>
      )}
      {ws.timeline && (
        <span className="inline-flex items-center gap-1.5">
          <span className="text-text-muted">{t("workspace.prefsBar.timeline")}</span>
          <span className="font-medium text-text">
            {t(`auth.prefs.timelineOpts.${ws.timeline}`, { defaultValue: ws.timeline })}
          </span>
        </span>
      )}
      {priorities.length > 0 && (
        <span className="inline-flex items-center gap-1.5">
          <span className="text-text-muted">{t("workspace.prefsBar.priorities")}</span>
          <span className="font-medium text-text">
            {priorities
              .map((p) => t(`auth.prefs.priorityOpts.${p}`, { defaultValue: p }))
              .join(", ")}
          </span>
        </span>
      )}
      <button
        onClick={openEdit}
        data-tour-id="workspace.prefsBar.edit"
        className="ml-auto rounded-md border border-border bg-surface px-2 py-0.5 text-[11px] font-medium text-text-sec hover:bg-surface-3 hover:text-text"
      >
        {t("workspace.prefsBar.edit")}
      </button>
    </div>
    {editModalSlot}
    </>
  );
}
